import ApiRoutes from "../constants/ApiRoutes";
import Constants from "../constants/Constants";
import ApiClient from "../utilities/api-client";
import AuthenticationService from "./authentication-service";

const CartService = {
    items : [],
    totalCartValue : 0,
    totalCartQty : 0,

    /** -----------------------------------------------------
     * Method : checkItemExists
     * Description : Method to check whether an item already exists in cart
     * @param item
     * @returns Item Index
     */
    checkItemExists : (item) => {
        return CartService.items.findIndex(i => {
            return i.id === item.id
        });
    },

    /** -----------------------------------------------------
     * Method : calculateCartTotalValueAndQty
     * Description : Method to calculate the total value of all items
     * @returns Cart Items, Total Cart Value
     */
    calculateCartTotalValueAndQty : () => {
        CartService.totalCartValue = 0;
        CartService.totalCartQty = 0;
        
        CartService.items.forEach(item => {
            if(item.quantity > 0) {
                CartService.totalCartValue += Number(item.totalCost);
            }
            CartService.totalCartQty += Number(item.quantity);
        });
    },

    /** -----------------------------------------------------
     * Method : getCart
     * Description : Method to get items in the cart and its total value
     * @returns Cart Items, Total Cart Value
     */
    getCart : () => {
        if(CartService.totalCartQty === 0) {
            // Check if available in Local Storage
            const cart = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.CART));
            if(cart && cart.totalCartQty > 0) {
                CartService.items = cart.items;
                CartService.totalCartQty = cart.totalCartQty;
                CartService.totalCartValue = cart.totalCartValue;
            }
        }
        return {
            items: CartService.items,
            totalCartQty : CartService.totalCartQty,
            totalCartValue: CartService.totalCartValue
        }
    },

    /** -----------------------------------------------------
     * Method : updateCart
     * Description : Method to construct the client side cart
     * @param item
     * @param qty 
     * @returns Cart Count, Total Cart Value
     */
    updateCart : async (item, quantity) => {
        const itemIndex = CartService.checkItemExists(item);
        item.quantity = Number(quantity);
        item.totalCost = item.quantity * Number(item.price);
        if(itemIndex >= 0) {
            if(quantity === 0) {
                // Remove item from cart
                CartService.items.splice(itemIndex, 1);
            } else {
                CartService.items[itemIndex] = item;
            }
        } else {
            CartService.items.push(item);
        }
        CartService.calculateCartTotalValueAndQty();
        await CartService.storeCartItemsLocally();
        return CartService.getCart();
    },

    /*** -------------------------------------------
     * Method : storeCartItemsLocally
     * Description : Store Cart Locally
     */
    storeCartItemsLocally : () => {
        const cart = {
            items : CartService.items, 
            totalCartQty : CartService.totalCartQty, 
            totalCartValue : CartService.totalCartValue,
            updateTime : new Date()
        }
        localStorage.setItem(Constants.LOCAL_STORAGE.CART, JSON.stringify(cart));
    },

    /** -----------------------------------------------------
     * Method : clearCart
     * Description : clear items in the cart
     */
    clearCart : async() => {
        CartService.items = [];
        CartService.totalCartQty = 0;
        CartService.totalCartValue = 0;
        CartService.storeCartItemsLocally();
    },

    /** -----------------------------------------------------
     * Method : prepareItemsForBuying
     * Description : Format items as per payload 
     * @param {*} items 
     */
    prepareItemsForBuying : async (items, isEventCash, currentBalance, cartTotalValue, isDiffLocation, selectedLocationDetails) => {
        // *** Combined Payment logic ***
        /* var processedItems = [];
        if(!isEventCash) {
            processedItems = items.map(item => {
                return {
                    id : item.id,
                    quantity : item.quantity,
                }
            });
        } else {
            let prevEventAmount = currentBalance.event.amount;
            let prevCdwAmount = currentBalance.cdw.amount;
            for(let item of items) {
                let totalPrice = item.quantity * item.price;
                let prevAmount = prevEventAmount;
                prevEventAmount = prevEventAmount - totalPrice;
                if(prevEventAmount < 0) {
                    prevCdwAmount = prevCdwAmount + prevEventAmount;
                    processedItems.push(
                        {
                            id : item.id,
                            quantity : item.quantity,
                            combinedPayment: {cdwCashAmount: -prevEventAmount, eventCashAmount: prevAmount},
                        }
                    )
                } else {
                    processedItems.push(
                        {
                            id : item.id,
                            quantity : item.quantity,
                            cashType: "event"
                        }
                    )
                }
            }
        }
        let isCombinedPayment = false;
        if(isEventCash) isCombinedPayment = true;
        return {
            isCombinedPayment,
            items: processedItems
        } */
        const processedItems = items.map(item => {
            return {
                id : item.id,
                quantity : item.quantity
            }
        });
        return {
            cashType: isEventCash && currentBalance.event && currentBalance.event.amount > 0 ? currentBalance.event.name : Constants.TYPE.CDW.toLowerCase(),
            currentLocationId: selectedLocationDetails.location_id,
            items: processedItems
        }
    },

    /** -----------------------------------------------------
     * Method : buyItems
     * @param {*} items 
     */
    buyItems : async (items, isEventCash, currentBalance, cartTotalValue, isDiffLocation, selectedLocationDetails) => {
        if(items.length > 0) {
            const payload = await CartService.prepareItemsForBuying(items, isEventCash, currentBalance, cartTotalValue, isDiffLocation, selectedLocationDetails);
            const url = `${ApiRoutes.EMPLOYEE.ROOT_CONTEXT}${ApiRoutes.EMPLOYEE.BUY_ITEMS}`;
            try {
                const response = await ApiClient.post(url, payload);
                if(response?.data?.isSuccess) {
                    const balance = response?.data?.currentBalance;
                    AuthenticationService.setUserBalance(balance);
                    await CartService.clearCart();
                    return true;
                }
            } catch (e) {
                throw e;
            }
            return false;
        }
    },

    /** -----------------------------------------------------
     * Method : updateMissingItems
     * Desc : find out the missing item and update is qty to 0 and mark as removable
     * @param {*} missingItems 
     */
    updateMissingItems : async(missingItems) => {
        CartService.items.forEach((item, index) => {
            if(missingItems.includes(item.id)) {
                CartService.items[index].quantity = 0;
                CartService.items[index].isRemovable = true;
            }
        });
        CartService.calculateCartTotalValueAndQty();
        await CartService.storeCartItemsLocally();
        return {
            totalCartQty : CartService.totalCartQty,
            totalCartValue: CartService.totalCartValue
        }
    },

    /** -----------------------------------------------------
     * Method : removeItemFromCart
     * Desc : remove item from cart
     * @param {*} itemId 
     */
    removeItemFromCart : async(itemId) => {
        CartService.items.forEach((item, index) => {
            if(item.id === itemId) {
                CartService.items.splice(index, 1);
            }
        });
        CartService.calculateCartTotalValueAndQty();
        await CartService.storeCartItemsLocally();
    }
}

export default CartService;